import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { useAuth } from "../context/AuthContext";
import { useBilling } from "../context/BillingContext";
import { api, apiError } from "../lib/api";
import { AppShell, PageHeader } from "../components/AppShell";
import { Button } from "../components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../components/ui/dialog";
import { toast } from "sonner";
import { FileText, Receipt, Landmark, ShieldCheck, LogOut, ChevronRight, Trash2, AlertTriangle, Sparkles, CreditCard, FlaskConical, Lock, Shield, Plug, Database, CloudOff, LifeBuoy } from "lucide-react";
import { LegalFooter } from "../components/LegalFooter";
import { isNativePlatform, showBillingUI, expiredRedirectPath, showDevTools } from "../lib/platform";
import { manageAppleSubscriptions } from "../lib/appleIap";

// Owner-only one-time Stripe link restoration: dry run first, then explicit confirmation.
const RESTORE_MSG = {
  no_match: "No single active live subscription matched this account. Nothing was changed.",
  ambiguous: "More than one active subscription matched. Nothing was changed.",
  conflict: "The subscription is linked elsewhere or this account has another link. Nothing was changed.",
  not_live: "The matched subscription is not in the live environment. Nothing was changed.",
  already_linked: "Your subscription is already linked.",
  restored: "Subscription link restored.",
};

export function StripeLinkRestore({ onDone }) {
  const [state, setState] = useState(null);
  const [busy, setBusy] = useState(false);
  const run = async (confirm) => {
    setBusy(true);
    try {
      const { data } = await api.post("/billing/stripe-link/restore", { confirm });
      setState(data);
      if (confirm && data.status === "restored") { toast.success(RESTORE_MSG.restored); onDone?.(); }
    } catch (e) { toast.error(apiError(e)); }
    setBusy(false);
  };
  const ready = state?.status === "ready";
  return (
    <div className="df-card p-4 mt-2" data-testid="stripe-link-restore">
      <div className="text-sm font-semibold text-slate-900 mb-1">Web subscription link</div>
      <p className="text-xs text-slate-500 mb-3" data-testid="stripe-link-restore-message">
        {!state ? "Check Stripe for an existing live subscription belonging to this account. Nothing changes until you confirm."
          : ready ? `Found 1 active subscription (${state.cancel_scheduled ? `cancels ${new Date(state.current_period_end).toLocaleDateString()}` : "renews monthly"}). Confirm to link it to this account. Stripe is not modified.`
          : RESTORE_MSG[state.status] || "Nothing was changed."}
      </p>
      {ready ? (
        <Button data-testid="stripe-link-restore-confirm" onClick={() => run(true)} disabled={busy}
          className="w-full h-10 rounded-xl bg-[#0D786A] hover:bg-[#0A6055] df-pressable">
          {busy ? "Linking…" : "Confirm and restore link"}
        </Button>
      ) : (
        <Button variant="outline" data-testid="stripe-link-restore-check" onClick={() => run(false)} disabled={busy}
          className="w-full h-10 rounded-xl df-pressable">
          {busy ? "Checking…" : "Check for existing subscription"}
        </Button>
      )}
    </div>
  );
}

// Native subscription actions. Never assumes eligibility when the status couldn't be loaded.
// Active Stripe: nothing to buy (no duplicate payment). Active Apple: manage. Otherwise: subscribe.
export function NativeSubscriptionActions({ billing, loading, onRetry, onSubscribe }) {
  const btn = "w-full h-10 rounded-xl mt-3 df-pressable";
  if (loading) return <p className="text-xs text-slate-400 mt-3" data-testid="ios-billing-loading">Checking subscription…</p>;
  if (!billing) {
    return (
      <div className="mt-3 text-xs text-amber-700" data-testid="ios-billing-unavailable">
        We couldn't confirm your subscription status.
        <Button variant="outline" data-testid="ios-billing-retry" onClick={onRetry} className={btn}>Retry</Button>
      </div>
    );
  }
  if (billing.billing_source === "stripe") return null;
  if (billing.billing_source === "apple") {
    return (
      <Button variant="outline" data-testid="ios-manage-subscription" className={btn}
        onClick={() => manageAppleSubscriptions().catch(() => toast.error("Couldn't open App Store subscriptions."))}>
        Manage subscription
      </Button>
    );
  }
  return (
    <Button data-testid="ios-subscribe-button" onClick={onSubscribe} className={`${btn} bg-[#0D786A] hover:bg-[#0A6055]`}>
      Subscribe
    </Button>
  );
}

export default function More() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { logout, user } = useAuth();
  const { billing, loading: billingLoading, refresh } = useBilling();
  const [resetOpen, setResetOpen] = useState(false);
  const [resetting, setResetting] = useState(false);
  const [loadingSample, setLoadingSample] = useState(false);
  const [portalLoading, setPortalLoading] = useState(false);
  const [togglingPaywall, setTogglingPaywall] = useState(false);
  const devToolsVisible = showDevTools();

  const togglePaywall = async () => {
    setTogglingPaywall(true);
    try {
      const lock = !billing?.test_paywall;
      await api.post("/billing/test/paywall", { lock });
      await refresh();
      if (lock) { toast.success("Paywall preview on"); navigate("/paywall"); }
      else toast.success("Back to normal access");
    } catch (e) { toast.error(apiError(e)); }
    setTogglingPaywall(false);
  };

  const manageBilling = async () => {
    setPortalLoading(true);
    try {
      const { data } = await api.post("/billing/portal", { origin_url: window.location.origin });
      window.location.href = data.portal_url;
    } catch (e) { toast.error(apiError(e)); setPortalLoading(false); }
  };

  const subStatusLabel = () => {
    if (!billing) return "";
    if (billing.subscription_status === "active") return "Active";
    if (billing.subscription_status === "past_due") return "Payment overdue";
    if (billing.subscription_status === "canceled") return "Cancelled";
    if (billing.in_trial) return `Free trial · ${billing.trial_days_left} day${billing.trial_days_left === 1 ? "" : "s"} left`;
    return "No active subscription";
  };
  const renewText = () => {
    if (billing?.subscription_status === "active" && billing?.current_period_end) {
      const d = new Date(billing.current_period_end);
      return `${billing.cancel_at_period_end ? "Ends" : "Renews"} ${d.toLocaleDateString("en-AU", { day: "2-digit", month: "short", year: "numeric" })}`;
    }
    return `$${((billing?.plan_amount_cents ?? 1499) / 100).toFixed(2)} ${billing?.plan_currency || "AUD"} / month`;
  };

  const items = [
    { label: "Accountant", desc: "Generate and share your records", icon: FileText, onClick: () => navigate("/more/accountant"), active: true, premium: true, testId: "more-accountant" },
    { label: "BAS & GST", desc: "Quarterly GST summary at a glance", icon: Landmark, onClick: () => navigate("/more/bas"), active: true, premium: true, testId: "more-bas" },
    { label: "Tax & Super reserves", desc: "Track reserved vs suggested", icon: Landmark, onClick: () => navigate("/more/reserves"), active: true, premium: true, testId: "more-reserves" },
    { label: "Expenses", desc: "View and add your expenses", icon: Receipt, onClick: () => navigate("/money/expenses"), active: true, testId: "more-expenses" },
    { label: "Security", desc: "Two-factor authentication", icon: ShieldCheck, onClick: () => navigate("/more/security"), active: true, testId: "more-security" },
    { label: "Privacy & Data", desc: "Export, correct or delete your data", icon: Shield, onClick: () => navigate("/more/privacy"), active: true, testId: "more-privacy" },
    { label: "Help & Support", desc: "Get help with your account", icon: LifeBuoy, onClick: () => navigate("/support"), active: true, testId: "more-support" },
  ];

  const resetData = async () => {
    setResetting(true);
    try {
      await api.post("/account/reset");
      qc.clear();
      setResetOpen(false);
      toast.success("Your data has been reset");
      navigate("/", { replace: true });
    } catch (e) { toast.error(apiError(e)); }
    setResetting(false);
  };

  const loadSample = async () => {
    setLoadingSample(true);
    try {
      await api.post("/account/load-sample");
      qc.clear();
      toast.success("Sample data loaded");
      navigate("/", { replace: true });
    } catch (e) { toast.error(apiError(e)); }
    setLoadingSample(false);
  };

  return (
    <AppShell>
      <PageHeader title="More" subtitle={user?.name || user?.email} />
      <div className="px-5 space-y-2.5 df-fade-in">
        {/* Subscription */}
        <div className="df-card p-4" data-testid="subscription-card">
          <div className="flex items-center gap-3">
            <span className="h-10 w-10 rounded-full bg-[#E6F4F1] text-[#0D786A] flex items-center justify-center"><CreditCard size={18} /></span>
            <div className="flex-1">
              <div className="font-medium text-slate-900">Subscription</div>
              <div className="text-xs text-slate-500" data-testid="subscription-status">{subStatusLabel()}{showBillingUI() ? ` · ${renewText()}` : ""}</div>
            </div>
          </div>
          {showBillingUI() && (
            <>
              {billing?.has_customer ? (
                <Button variant="outline" data-testid="manage-billing-button" onClick={manageBilling} disabled={portalLoading}
                  className="w-full h-10 rounded-xl mt-3 df-pressable">
                  {portalLoading ? "Opening…" : "Manage or cancel"}
                </Button>
              ) : (
                <Button data-testid="subscribe-button" onClick={() => navigate("/paywall")}
                  className="w-full h-10 rounded-xl mt-3 bg-[#0D786A] hover:bg-[#0A6055] df-pressable">
                  Subscribe — ${((billing?.plan_amount_cents ?? 1499) / 100).toFixed(2)}/mo
                </Button>
              )}
              <p className="text-[11px] text-slate-400 leading-relaxed mt-2">
                Cancel anytime. Access continues to the end of your paid billing period and future charges stop.
                The 14-day free trial applies once, before your first payment.
              </p>
            </>
          )}
          {isNativePlatform() && <NativeSubscriptionActions billing={billing} loading={billingLoading} onRetry={refresh} onSubscribe={() => navigate("/paywall")} />}
        </div>

        {items.map((it) => {
          const Icon = it.icon;
          const locked = it.premium && !billing?.premium;
          return (
            <button key={it.label} data-testid={it.testId} onClick={locked ? () => navigate(expiredRedirectPath()) : it.onClick} disabled={!it.active}
              className={`w-full flex items-center gap-3 df-card p-4 df-pressable ${it.active ? "" : "opacity-70 cursor-default"}`}>
              <span className={`h-10 w-10 rounded-full flex items-center justify-center ${it.active ? "bg-[#E6F4F1] text-[#0D786A]" : "bg-slate-100 text-slate-400"}`}>
                <Icon size={18} />
              </span>
              <span className="flex-1 text-left">
                <span className="block font-medium text-slate-900">{it.label}</span>
                <span className="block text-xs text-slate-500">{locked ? "Subscribe to unlock" : it.desc}</span>
              </span>
              {locked ? <Lock size={16} className="text-slate-300" /> : it.active && <ChevronRight size={18} className="text-slate-300" />}
            </button>
          );
        })}

        <div className="df-card p-4 mt-2" data-testid="compliance-card">
          <div className="text-sm font-semibold text-slate-900 mb-1">Australian compliance</div>
          <p className="text-xs text-slate-500 leading-relaxed">
            Molaroo helps you organise income, payments and expenses in AUD using the Australian financial year (1 Jul – 30 Jun).
            It does not provide tax, accounting, legal or financial advice, does not lodge BAS, and does not determine GST,
            superannuation or employment/contractor status. GST figures are estimates for BAS preparation only. You remain
            responsible for your own tax obligations and AHPRA professional obligations. No patient or health information is collected.
          </p>
        </div>

        {/* Data & Backup — native local-first status */}
        {isNativePlatform() && (
          <div className="df-card p-4 mt-2" data-testid="data-backup-card">
            <div className="text-sm font-semibold text-slate-900 mb-2">Data & Backup</div>
            <div className="flex items-center gap-3 py-1.5" data-testid="local-db-status">
              <span className="h-9 w-9 rounded-full bg-[#E6F4F1] text-[#0D786A] flex items-center justify-center"><Database size={16} /></span>
              <div className="flex-1">
                <div className="text-sm font-medium text-slate-900">On-device database</div>
                <div className="text-xs text-emerald-700">Encrypted · stored on this device</div>
              </div>
            </div>
            <div className="flex items-center gap-3 py-1.5" data-testid="cloud-backup-status">
              <span className="h-9 w-9 rounded-full bg-slate-100 text-slate-400 flex items-center justify-center"><CloudOff size={16} /></span>
              <div className="flex-1">
                <div className="text-sm font-medium text-slate-900">Cloud backup</div>
                <div className="text-xs text-slate-500">Not configured</div>
              </div>
            </div>
            <p className="text-[11px] text-slate-400 leading-relaxed mt-2">
              Your financial records are kept in an encrypted database on this device. Encrypted, user-controlled
              cloud backup will be available in a future update. Until then, keep your device backed up.
            </p>
          </div>
        )}

        {/* Sample data */}
        {devToolsVisible && (
        <div className="df-card p-4 mt-2" data-testid="sample-data-card">
          <div className="text-sm font-semibold text-slate-900 mb-1">Preview with sample data</div>
          <p className="text-xs text-slate-500 leading-relaxed mb-3">
            Load a set of example practices, work days and payments to explore how Molaroo works.
            This replaces your current data — use "Reset all my data" below to clear it again.
          </p>
          <Button variant="outline" data-testid="load-sample-button" onClick={loadSample} disabled={loadingSample}
            className="w-full h-11 rounded-xl border-[#0D786A]/30 text-[#0D786A] hover:bg-[#E6F4F1] df-pressable">
            <Sparkles size={16} className="mr-1.5" /> {loadingSample ? "Loading…" : "Load sample data"}
          </Button>
        </div>
        )}

        {/* Testing tools */}
        {devToolsVisible && (
        <div className="df-card p-4 mt-2" data-testid="test-tools-card">
          <div className="flex items-center gap-1.5 text-sm font-semibold text-slate-900 mb-1">
            <FlaskConical size={15} className="text-slate-400" /> Testing tools
          </div>
          <p className="text-xs text-slate-500 leading-relaxed mb-3">
            Preview what a pilot user sees when their free trial has ended. This only changes your own view and can be switched off any time.
          </p>
          <Button variant="outline" data-testid="toggle-paywall-button" onClick={togglePaywall} disabled={togglingPaywall}
            className="w-full h-11 rounded-xl df-pressable">
            {togglingPaywall ? "Updating…" : billing?.test_paywall ? "Exit paywall preview" : "Preview paywall (force trial ended)"}
          </Button>
        </div>
        )}

        {/* NavScora integration — owner only */}
        {!isNativePlatform() && user?.is_owner && billing && !billing.has_customer && <StripeLinkRestore onDone={refresh} />}
        {user?.is_owner && (
          <button data-testid="more-navscora" onClick={() => navigate("/more/navscora")}
            className="w-full flex items-center gap-3 df-card p-4 df-pressable mt-2">
            <span className="h-10 w-10 rounded-full bg-[#E6F4F1] text-[#0D786A] flex items-center justify-center"><Plug size={18} /></span>
            <span className="flex-1 text-left">
              <span className="block font-medium text-slate-900">NavScora Integration</span>
              <span className="block text-xs text-slate-500">Preview & sync commercial snapshot</span>
            </span>
            <ChevronRight size={18} className="text-slate-300" />
          </button>
        )}

        {/* Danger zone */}
        <div className="df-card p-4 mt-2 border border-red-100" data-testid="danger-zone">
          <div className="text-sm font-semibold text-slate-900 mb-1">Reset my data</div>
          <p className="text-xs text-slate-500 leading-relaxed mb-3">
            Permanently delete all your practices, work, payments, expenses and reserves so you can start fresh.
            Your account and sign-in stay the same. This can't be undone.
          </p>
          <Button variant="outline" data-testid="reset-data-button" onClick={() => setResetOpen(true)}
            className="w-full h-11 rounded-xl border-red-200 text-red-600 hover:bg-red-50 df-pressable">
            <Trash2 size={16} className="mr-1.5" /> Reset all my data
          </Button>
        </div>

        <button data-testid="logout-button" onClick={logout}
          className="w-full flex items-center gap-3 df-card p-4 df-pressable mt-4">
          <span className="h-10 w-10 rounded-full bg-red-50 text-red-500 flex items-center justify-center"><LogOut size={18} /></span>
          <span className="flex-1 text-left font-medium text-red-600">Sign out</span>
        </button>

        <p className="text-center text-xs text-slate-400 pt-4">Molaroo <span className="text-slate-500">by NavScora</span> · organises your financial records — not tax, accounting, legal or financial advice.</p>
        <LegalFooter className="pt-2 pb-2" />
      </div>

      <Dialog open={resetOpen} onOpenChange={setResetOpen}>
        <DialogContent className="rounded-2xl max-w-sm">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600"><AlertTriangle size={18} /> Reset all data?</DialogTitle>
            <DialogDescription>
              This permanently deletes every practice, work day, payment, expense and reserve on your account.
              Your login stays the same. This action can't be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setResetOpen(false)} className="rounded-xl">Cancel</Button>
            <Button data-testid="reset-data-confirm" onClick={resetData} disabled={resetting}
              className="rounded-xl bg-red-600 hover:bg-red-700">{resetting ? "Resetting…" : "Yes, reset everything"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </AppShell>
  );
}
